// Thọ nguyên và Tử Quan (CULTIVATION §1.2, §5; BALANCE §1.5, §2.3). Tuổi chỉ tăng vì lựa chọn
// chủ động; Tử Quan trả trước LIFESPAN_BY_REALM[r] × giờ / 300 năm; sàn 5% thọ tối đa (P0–P2).
import { BALANCE } from '@data/balance';
import { siteDef } from '@data/content/sites';
import { HOUR_MS } from '../core/time';
import { stepInfo } from './steps';
import type { CultivationState } from './types';

export function lifespanMax(state: CultivationState): number {
  const base = BALANCE.LIFESPAN_BY_REALM[stepInfo(state.step).realmIdx] ?? 0;
  return base + state.lifespan.lifespanBonus;
}

export function lifespanRemaining(state: CultivationState): number {
  return lifespanMax(state) - state.lifespan.age;
}

export function deathSeclusionCost(state: CultivationState, hours: number): number {
  const base = BALANCE.LIFESPAN_BY_REALM[stepInfo(state.step).realmIdx] ?? 0;
  return base * hours * BALANCE.DEATH_SECLUSION_LIFESPAN_RATIO_PER_H;
}

export type DeathSeclusionBlock = 'duration' | 'active' | 'lifespanFloor';

export function deathSeclusionBlock(state: CultivationState, hours: number): DeathSeclusionBlock | null {
  if (!(BALANCE.DEATH_SECLUSION_DURATIONS_H as readonly number[]).includes(hours)) return 'duration';
  if (state.idle.seclusion.kind === 'death') return 'active';
  const max = lifespanMax(state);
  const left = max - state.lifespan.age - deathSeclusionCost(state, hours);
  if (left < BALANCE.LIFESPAN_FLOOR_RATIO * max) return 'lifespanFloor';
  return null;
}

/** Vào Tử Quan tại nơi bế quan hiện tại. Gọi sau khi đã quyết toán tới `nowMs`. */
export function startDeathSeclusion(state: CultivationState, hours: number, nowMs: number): CultivationState {
  const block = deathSeclusionBlock(state, hours);
  if (block) throw new Error(`cannot start death seclusion: ${block}`);
  const cost = deathSeclusionCost(state, hours);
  return {
    ...state,
    idle: {
      ...state.idle,
      seclusion: {
        kind: 'death',
        startedAtMs: nowMs,
        endsAtMs: nowMs + hours * HOUR_MS,
        lifespanPrepaid: cost,
      },
    },
    lifespan: {
      ...state.lifespan,
      age: state.lifespan.age + cost,
      history: [...state.lifespan.history, { atMs: nowMs, delta: cost, source: 'deathSeclusion' }],
    },
  };
}

export type SiteChangeBlock = 'unknown' | 'deathSeclusion' | 'same';

export function siteChangeBlock(state: CultivationState, siteId: string): SiteChangeBlock | null {
  try {
    siteDef(siteId);
  } catch {
    return 'unknown';
  }
  if (state.idle.seclusion.kind === 'death') return 'deathSeclusion';
  if (state.idle.locationId === siteId) return 'same';
  return null;
}

export function changeSite(state: CultivationState, siteId: string): CultivationState {
  const block = siteChangeBlock(state, siteId);
  if (block) throw new Error(`cannot change site: ${block}`);
  return { ...state, idle: { ...state.idle, locationId: siteId } };
}
